/**
 * Room codes — SRS B4.
 *
 * Six digits, never starting with zero, unique among currently active sessions.
 * A leading zero is excluded because students type these on a numeric keypad and
 * leading zeros get dropped, mistyped, and argued about.
 */
export const ROOM_CODE_LENGTH = 6;
const MIN_CODE = 100_000;
const MAX_CODE = 999_999;
const RANGE = MAX_CODE - MIN_CODE + 1;
/** Exactly six digits, first digit 1–9. */
export const ROOM_CODE_PATTERN = /^[1-9]\d{5}$/;
export function isValidRoomCode(code) {
    return ROOM_CODE_PATTERN.test(code);
}
/** Strip spaces and dashes so a pasted code like "482 913" still works. */
export function normaliseRoomCode(input) {
    return input.replace(/[\s-]/g, '');
}
/**
 * Uniformly distributed random integer in [0, max).
 * Rejection sampling, so the top of the range is not slightly less likely than the
 * bottom — which a plain modulo would cause.
 */
function randomBelow(max) {
    const limit = Math.floor(0xffffffff / max) * max;
    const buffer = new Uint32Array(1);
    let value;
    do {
        globalThis.crypto.getRandomValues(buffer);
        value = buffer[0] ?? 0;
    } while (value >= limit);
    return value % max;
}
/** One candidate room code. Does not check uniqueness — that needs the database. */
export function generateRoomCode() {
    return String(MIN_CODE + randomBelow(RANGE));
}
/**
 * A code not currently in use.
 *
 * `isTaken` is supplied by the server and checks the unique index on active sessions.
 * With ~900k codes and a handful of concurrent classes, a collision is vanishingly
 * unlikely, so a small retry budget is plenty.
 */
export async function generateUniqueRoomCode(isTaken, maxAttempts = 10) {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const code = generateRoomCode();
        if (!(await isTaken(code)))
            return code;
    }
    throw new Error(`Could not find a free room code after ${maxAttempts} attempts`);
}
//# sourceMappingURL=room-code.js.map